import { useEffect } from 'react';

/**
 * SEO component for dynamic page metadata
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.path - Page path for canonical URL
 */
const SEO = ({ title, description, path = '', noindex = false }) => {
  const siteTitle = 'Jerusalem Holy Prayer';
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} | Your Prayer Delivered to the Holy Land`;
  const defaultDescription = 'Have your prayer placed on a physical scroll and delivered to Jerusalem\'s holiest sites. Receive photo proof from the Western Wall, Church of the Holy Sepulchre & Garden Tomb.';
  const baseUrl = 'https://jerusalemholyprayer.org';
  const ogImage = 'https://static.prod-images.emergentagent.com/jobs/f56254ff-e645-4c2a-9633-4b99ef2db2a9/images/e6a35e26bde5f44d60a39bf37dbe8a91d69893ff02043c94c992e9a6cd58573f.png';

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || defaultDescription);
    }

    // Update OG title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', fullTitle);
    }

    // Update OG description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description || defaultDescription);
    }

    // Update Twitter title
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', fullTitle);
    }

    // Update Twitter description
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description || defaultDescription);
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${baseUrl}${path}`);
    }

    // Update OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${baseUrl}${path}`);
    }

    // Update OG Image
    const ogImageMeta = document.querySelector('meta[property="og:image"]');
    if (ogImageMeta) {
      ogImageMeta.setAttribute('content', ogImage);
    }

    // Update Twitter URL
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', `${baseUrl}${path}`);
    }

    // Robots noindex/nofollow for private/customer-data pages.
    // Removed automatically when component unmounts so it doesn't leak to other routes.
    let robotsMeta = document.querySelector('meta[name="robots"]');
    let robotsMetaCreated = false;
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
        robotsMetaCreated = true;
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
    }
    return () => {
      if (noindex && robotsMeta) {
        if (robotsMetaCreated) {
          robotsMeta.remove();
        } else {
          robotsMeta.setAttribute('content', 'index, follow');
        }
      }
    };
  }, [fullTitle, description, path, defaultDescription, noindex]);

  return null;
};

export default SEO;
